<script setup>
import { isEmpty } from 'lodash'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { CHECKIN_MODAL_ENTRY_POINTS } from '@/utils/check-ins/check-ins'
import { BUTTON_SIZES } from '@/utils/components-configurations/app-button'
import {
  currentUserCanUpdateObjective,
  isOkrElementClosed,
  okrElementGradeIsEditable
} from '@/utils/objectives'
import { useMetricType, useMetricValues } from '@/utils/okr-element-composables'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import { CHECKIN_MODAL_TRIGGER_FROM_VALUE_BUTTON_TEST_ID } from '@/components/check-ins/jest-helpers'
import BinaryValueSwitch from '@/components/form/element-progress-preview/BinaryValueSwitch'
import ProgressPreviewValueField from '@/components/form/element-progress-preview/ProgressPreviewValueField'
import FormFieldNext from '@/components/form/FormFieldNext'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'ElementProgressPreview'
})

const props = defineProps({
  element: {
    type: Object,
    required: true
  }
})

const { t } = useI18n()

const { isBinary, isStartTarget } = useMetricType({
  element: () => props.element
})

const { getOriginalValue, getTargetValue, getBinaryValue, getCurrentValue, unit } = useMetricValues(
  {
    element: () => props.element
  }
)

const binarySwitchIncrement = ref(0)

const emit = defineEmits({
  'open-checkin-modal': null
})

const hasUpdateAccess = computed(() => currentUserCanUpdateObjective(props.element))

const openCheckinModal = newValue => {
  if (!hasUpdateAccess.value) return

  const payload = {
    okrElement: {
      ...props.element
    },
    entryPoint: CHECKIN_MODAL_ENTRY_POINTS.CREATE
  }
  if (isBinary.value) {
    // this is a workaround to force re-render of the binary switch
    // coz the radio buttons browser manages checked state
    // and even if the model was not updated after the user clicks on the radio button,
    // the browser will not update the checked state, and we will see checked different radio button
    // to a real model so force re-render is faster solution to rollback the browser state equal to the model state
    binarySwitchIncrement.value += 1
  }

  if (
    isBinary.value &&
    !isOkrElementClosed(props.element) &&
    okrElementGradeIsEditable(props.element) &&
    isEmpty(props.element.dataSourceQuery)
  ) {
    // payload.okrElement[METRIC_VALUES_KEYS.CURRENT_VALUE] = newValue
    payload.overrideCurrentValue = newValue
  }

  emit('open-checkin-modal', payload)
}
</script>

<template>
  <div class="epp-Preview">
    <div class="epp-Preview_Fields">
      <ProgressPreviewValueField
        v-if="isStartTarget"
        :label="t('create.keyResult.label.originalValue')"
        :model-value="getOriginalValue"
        :suffix="unit"
        class="epp-Input"
        data-auto-testid="start-value"
        data-testid="start-value"
      />

      <FormFieldNext :disabled="!hasUpdateAccess" :label="t('common.value')" class="epp-Field">
        <BinaryValueSwitch
          v-if="isBinary"
          :key="`binary-switch-${binarySwitchIncrement}`"
          :data-auto-testid="CHECKIN_MODAL_TRIGGER_FROM_VALUE_BUTTON_TEST_ID"
          :data-testid="CHECKIN_MODAL_TRIGGER_FROM_VALUE_BUTTON_TEST_ID"
          :disabled="!hasUpdateAccess"
          :model-value="getBinaryValue"
          :name="`element-${element[OKR_ELEMENT_KEYS.ID] || 'unknown_id'}-form-binary-type-switch`"
          @update:model-value="openCheckinModal"
        />

        <AppButton
          v-if="isStartTarget"
          :class="{
            'epp-Button-readonly': !hasUpdateAccess
          }"
          :data-auto-testid="CHECKIN_MODAL_TRIGGER_FROM_VALUE_BUTTON_TEST_ID"
          :data-testid="CHECKIN_MODAL_TRIGGER_FROM_VALUE_BUTTON_TEST_ID"
          :disable="!hasUpdateAccess"
          :size="BUTTON_SIZES.LG_NEXT"
          class="epp-Button"
          type="none"
          @click="openCheckinModal(null)"
        >
          <span class="oboard-truncated-text">
            {{ getCurrentValue }}
          </span>
          <AppIcon
            v-if="hasUpdateAccess"
            class="epp-Button_Icon"
            height="24"
            icon-name="chevron-right-next"
            width="24"
          />
        </AppButton>
      </FormFieldNext>

      <ProgressPreviewValueField
        v-if="isStartTarget"
        :label="t('create.keyResult.label.targetValue')"
        :model-value="getTargetValue"
        :suffix="unit"
        class="epp-Input"
        data-auto-testid="target-value"
        data-testid="target-value-field"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/result-type-settings';

.epp-Preview {
  padding: 18px;
}

.epp-Preview_Fields {
  max-width: $fullscreen-result-type-settings-max-width;
  margin-inline: auto;
  display: flex;
  gap: 13px;
}

.epp-Input {
  --font-size: #{$fs-14};
}

.epp-Button {
  --button-high-padding: 8px;
  font-weight: fw('semi-bold');
  flex-shrink: 0;
  --justify-content: flex-start;
  line-height: normal;
  border: 2px solid $grey-2-next;
  transition: border-color $transition-fast ease-in-out,
    background-color $transition-fast ease-in-out;

  &:not(&-readonly) {
    @media (any-hover: hover) {
      &:hover {
        padding-right: 2px;
        border-color: $grey-1-next;
        background-color: $grey-3-next;
      }
    }

    &:active {
      border-color: $primary-color-next;
    }
  }

  &-readonly {
    border-color: $grey-2-next;
    background: $grey-2-next;
    color: $dark-3;
  }
}

.epp-Field {
  overflow: hidden;
}

.epp-Input,
.epp-Field {
  flex: 1 0 0;
}

.epp-Button_Icon {
  color: $primary-color-next;
  margin-left: auto;
  flex-shrink: 0;

  &:not(.epp-Button:hover &) {
    display: none;
  }
}
</style>
